// src/sim/ai/actions.ts — BT leaf action library for hunter (and future role) trees.
//
// All functions are called with `this` bound to an AgentContext object by mistreevous.
// Each action mutates `this.intent` scratch and returns a State constant.
//
// Actions MUST only read from `this.perception` (Pitfall P4-C: `this.world` is `never`).
// Movement actions write `this.intent.move` as a unit vector.
// One-shot flag actions write a single `this.intent.actions.*` boolean.
//
// NOTE: Several actions are stubs in Plan 05 — pathing integration lands in Plan 10.
// Stubs return SUCCEEDED so the branch doesn't stall during early development.
//
// Source: .planning/phases/04-ai/04-05-PLAN.md Task 1 action;
//         04-CONTEXT.md D-08, D-40, D-43; 04-RESEARCH.md Pattern 3.

import { State } from 'mistreevous';
import type { AgentContext } from './ai-controller-types';
import type { CharacterProfile } from '../characters/character-profile';
import type { Entity } from '../../shared/types/world';
import type { EventTag } from '../../shared/types/events';
import type { MapData } from '../../shared/types/map';
import { sampleJitteredTarget } from './blackboard';
import { getCommentary } from '../npcs/adults/commentary';
import { findTilePath } from '../map/pathfinding';
import { zoneCenter } from '../map/zones';
// P4_LISTEN_STILL_VELOCITY_PX_PER_S (8 px/s) is documented in effectiveHearingRangeTiles JSDoc.
// The caller computes stationarity and passes it as a boolean; the constant is named in constants.ts.

// Re-export State so test files can import it from here if needed
export { State };

/**
 * UAT Test 2 fix (hunters-frozen-at-spawn follow-up): compute the unit-vector
 * direction from `from` toward the NEXT A* waypoint en route to `to`. The
 * pre-existing Path* actions used direct (from → to) unit vectors which
 * wall-clamped to zero as soon as a building blocked line-of-sight, stranding
 * hunters against the first obstacle between them and any target. Routing
 * through A* produces a vector that navigates around walls.
 *
 * Returns null when:
 *   - from/to tile is off-grid,
 *   - no path exists (unreachable — usually target inside a building), or
 *   - both endpoints lie in the same tile (caller treats this as "arrived").
 *
 * Callers fall through to FAILED on null so the BT selector tries another
 * branch rather than spinning forever on an unreachable target.
 *
 * PF.Grid is rebuilt fresh per call (Pitfall 1 in src/sim/map/pathfinding.ts).
 * At the 10 Hz hunter decide cadence × ~1–8 hunters this is cheap on the
 * 160×144 map; if perf ever matters, cache paths per-agent keyed by
 * (fromTile, toTile).
 */
function aStarMoveVector(
  from: { x: number; y: number },
  to: { x: number; y: number },
  map: MapData,
): { x: number; y: number } | null {
  // Plan 06-13: defensive guard for test fixtures (and any future malformed
  // map) that pass an empty walkability grid. PF.Grid throws on undefined
  // first row; caller treats null as "unreachable" and falls through to
  // SUCCEEDED so the BT advances rather than crashing the tick.
  if (!map.grid || map.grid.length === 0 || !map.grid[0]) return null;
  const tileW = map.tileWidth;
  const tileH = map.tileHeight;
  const fx = Math.floor(from.x / tileW);
  const fy = Math.floor(from.y / tileH);
  const tx = Math.floor(to.x / tileW);
  const ty = Math.floor(to.y / tileH);
  if (fx === tx && fy === ty) return null;
  // Debug session cascade-prevention (2026-04-30): tell A* about the plinth
  // ring so paths route around it instead of through it. Without this, freed
  // hiders' deep/move targets routed through the (solid) ring stalled at the
  // boundary and never dispersed.
  const ringObstacle = ringObstacleFor(map);
  // Phase 08.1 Item 2: use movementGrid (walls + obstacles + fences) for pathfinding
  // so AI routes around low props/fences, not through them. Falls back to sight grid.
  const path = findTilePath(fx, fy, tx, ty, map.movementGrid ?? map.grid, ringObstacle);
  if (path.length < 2) return null;
  const [nx, ny] = path[1]!;
  const targetPx = {
    x: nx * tileW + tileW / 2,
    y: ny * tileH + tileH / 2,
  };
  const dx = targetPx.x - from.x;
  const dy = targetPx.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 0.001) return null;
  return { x: dx / len, y: dy / len };
}

/**
 * ringObstacleFor — build the plinth-ring obstacle descriptor for ring-aware A*
 * (findTilePath), or null when the map has no plinth zone. Factored so both
 * aStarMoveVector and the deep-branch reachability scan route around the SAME
 * solid ring (radius mirrors P3_PLINTH_RING_RADIUS_PX; no cross-tier import in
 * a BT action).
 */
function ringObstacleFor(map: MapData) {
  const plinth = map.zones.get('plinth');
  if (!plinth) return null;
  return {
    centerPx: zoneCenter(plinth),
    radiusPx: 48,
    tileWidthPx: map.tileWidth,
    tileHeightPx: map.tileHeight,
  };
}

/**
 * isSpotReachable — true iff a ring-aware A* path of length >= 2 exists from
 * `from` to `to`. The deep branch uses this to skip hiding spots it cannot
 * actually reach (a spot snapped onto, or authored at, a wall/enclosed tile).
 *
 * Without it the deep sequence picks the nearest spot by straight-line distance;
 * EnterHideSpot then calls aStarMoveVector which returns null (unreachable), no
 * intent.move is set, and — because the selector guards every branch with
 * while(IsWinningBranch, id) — a deep-preferring freed hider FREEZES at the ring
 * (debug session free-all-misfire-frozen-hider, BUG#2c 2026-06-11).
 */
function isSpotReachable(
  from: { x: number; y: number },
  to: { x: number; y: number },
  map: MapData,
): boolean {
  if (!map.grid || map.grid.length === 0 || !map.grid[0]) return false;
  const fx = Math.floor(from.x / map.tileWidth);
  const fy = Math.floor(from.y / map.tileHeight);
  const tx = Math.floor(to.x / map.tileWidth);
  const ty = Math.floor(to.y / map.tileHeight);
  if (fx === tx && fy === ty) return true; // already on the spot's tile
  // Phase 08.1 Item 2: use movementGrid so reachability check respects obstacles/fences.
  return findTilePath(fx, fy, tx, ty, map.movementGrid ?? map.grid, ringObstacleFor(map)).length >= 2;
}

/**
 * radialScatterAwayFromPlinth — a deterministic unit vector pointing FROM the
 * plinth centre toward `self` (i.e. radially outward). When a freed hider's
 * target is unreachable this guarantees it still walks AWAY from the ring
 * instead of freezing — the user-requested movement-based scatter, NOT a
 * position teleport. Falls back to a seeded random direction if the hider sits
 * exactly on the centre.
 */
function radialScatterAwayFromPlinth(
  self: { x: number; y: number },
  perception: AgentContext['perception'],
): { x: number; y: number } {
  const plinth = perception.map.zones.get('plinth');
  const center = plinth
    ? zoneCenter(plinth)
    : {
        x: (perception.map.widthInTiles * perception.map.tileWidth) / 2,
        y: (perception.map.heightInTiles * perception.map.tileHeight) / 2,
      };
  const dx = self.x - center.x;
  const dy = self.y - center.y;
  const len = Math.hypot(dx, dy);
  if (len < 0.001) {
    const a = perception.rng() * Math.PI * 2;
    return { x: Math.cos(a), y: Math.sin(a) };
  }
  return { x: dx / len, y: dy / len };
}

/**
 * pickWanderTarget — a uniformly-random pixel point on the map, sampled via the
 * per-agent seeded RNG (D-06 / Pitfall 10). Used as the deep branch's movement
 * fallback when no hiding spot is available (BUG#2b) so the hider never freezes.
 */
function pickWanderTarget(perception: AgentContext['perception']): { x: number; y: number } {
  const w = perception.map.widthInTiles * perception.map.tileWidth;
  const h = perception.map.heightInTiles * perception.map.tileHeight;
  return { x: perception.rng() * w, y: perception.rng() * h };
}

// ---------------------------------------------------------------------------
// Chase branch actions
// ---------------------------------------------------------------------------

/**
 * FaceNearestHider — rotates this.intent.facing toward the closest visible
 * FREE hider. Caught hiders are excluded so a hunter parked at plinth with a
 * just-dropped-off hider at their feet does not keep "chasing" the already-
 * caught target (UAT Test 2 follow-up — hunter stuck at plinth).
 * Returns FAILED when no free hiders are visible (guards the entire chase branch against
 * empty visible lists — Pitfall 4 belt-and-suspenders).
 */
export function FaceNearestHider(this: AgentContext): typeof State.SUCCEEDED | typeof State.FAILED {
  const tick = this.perception.tick;
  const hiders = this.perception.visible.filter(
    v => v.role === 'hider'
      && v.status !== 'caught'
      // Debug session cascade-prevention (2026-04-28): skip targets in the
      // freed-immunity window — catch.ts won't let us catch them anyway, and
      // chasing them stalls the hunter at the plinth, sustaining the cascade.
      && (v.freedImmunityUntilTick == null || tick >= v.freedImmunityUntilTick),
  );
  if (hiders.length === 0) return State.FAILED;
  const target = hiders.reduce((a, b) => a.distance < b.distance ? a : b);
  const dx = target.position.x - this.perception.self.position.x;
  const dy = target.position.y - this.perception.self.position.y;
  this.intent.facing = Math.atan2(dy, dx);
  return State.SUCCEEDED;
}

/**
 * PathTowardNearestHider — sets move intent toward the closest visible FREE
 * hider, routed around walls via A*. Caught hiders are excluded (see
 * FaceNearestHider rationale).
 */
export function PathTowardNearestHider(this: AgentContext): typeof State.SUCCEEDED | typeof State.FAILED {
  const tick = this.perception.tick;
  const hiders = this.perception.visible.filter(
    v => v.role === 'hider'
      && v.status !== 'caught'
      // Debug session cascade-prevention (2026-04-28): skip immune targets.
      && (v.freedImmunityUntilTick == null || tick >= v.freedImmunityUntilTick),
  );
  if (hiders.length === 0) return State.FAILED;
  const target = hiders.reduce((a, b) => a.distance < b.distance ? a : b);
  const self = this.perception.self.position;
  const dx = target.position.x - self.x;
  const dy = target.position.y - self.y;
  const len = Math.hypot(dx, dy);
  if (len < 0.001) return State.SUCCEEDED;
  const move = aStarMoveVector(self, target.position, this.perception.map);
  if (!move) {
    // Same tile or unreachable — caller treats SUCCEEDED as "nothing to move
    // toward". AttemptCatchIfInRange runs next in the sequence and handles
    // the close-range case.
    return State.SUCCEEDED;
  }
  this.intent.move = move;
  return State.SUCCEEDED;
}

/**
 * AttemptCatchIfInRange — sets catchAttempt flag if the nearest hider is within catch range.
 * 32 px = P1_CHARACTER_RADIUS * 2 catch-overlap distance (Plan 03 D-01).
 * Plan 05 stub: uses `shout` flag as a proxy for catch attempt (real flag added in Plan 10).
 * TODO(Plan 10): wire to a proper `catchAttempt` flag on IntentActions.
 */
export function AttemptCatchIfInRange(this: AgentContext): typeof State.SUCCEEDED | typeof State.FAILED {
  const hiders = this.perception.visible.filter(v => v.role === 'hider' && v.status !== 'caught');
  if (hiders.length === 0) return State.FAILED;
  const nearest = hiders.reduce((a, b) => a.distance < b.distance ? a : b);
  const CATCH_RANGE_PX = 64; // ~2 tiles, matches Plan 03 detectCatches threshold
  if (nearest.distance <= CATCH_RANGE_PX) {
    // TODO(Plan 10): intent.actions.catchAttempt = true; For now the action SUCCEEDS
    // and the sim's catch detection handles the catch via position proximity.
  }
  return State.SUCCEEDED;
}

// ---------------------------------------------------------------------------
// Investigate-sighting branch actions
// ---------------------------------------------------------------------------

/**
 * SampleBlackboardTarget — picks a jittered target near the first blackboard entry.
 * Writes the sampled position to this._sampledTarget (used by PathTowardSampledTarget).
 * Uses per-agent RNG from perception.rng for deterministic Gaussian jitter (D-14).
 */
export function SampleBlackboardTarget(this: AgentContext & { _sampledTarget?: { x: number; y: number } }): typeof State.SUCCEEDED | typeof State.FAILED {
  if (this.perception.memory.length === 0) {
    this.targetBlackboardEntryId = null;
    return State.FAILED;
  }
  const entry = this.perception.memory[0]!;
  // BLOCKER-1 (Plan 10): record which entry we're targeting so tick.ts can compute
  // duplicate-hunter penalty via _hunterTargetBlackboardEntryId.
  this.targetBlackboardEntryId = entry.id;
  this._sampledTarget = sampleJitteredTarget(entry, this.perception.tick, this.perception.rng);
  return State.SUCCEEDED;
}

/**
 * PathTowardSampledTarget — moves toward the previously sampled blackboard
 * target, routed around walls via A*.
 *
 * Returns RUNNING while in transit (whether via a valid A* vector or the
 * radial-scatter fallback for unreachable targets) and SUCCEEDED only when
 * the hider arrives (dist < 8 px).
 *
 * Why RUNNING matters: the "move" branch BT sequence is:
 *   PickQuietWaypoint → PathTowardSampledTarget
 * With SUCCEEDED the sequence COMPLETES each decide tick, causing
 * PickQuietWaypoint to re-run and pick a NEW random waypoint every 100 ms —
 * producing the "jittery" movement symptom reported in UAT round 4. With
 * RUNNING, mistreevous resumes from this node directly on the next decide
 * tick (skipping PickQuietWaypoint) so the hider commits to its waypoint
 * until it arrives, then lets the sequence restart with a fresh pick.
 *
 * The same change is safe for the "liberate" branch
 *   (HasCaughtTeammatesAtBase → PickPlinthApproach → PathTowardSampledTarget):
 * while RUNNING the while(IsWinningBranch, "liberate") decorator re-checks
 * every tick — if the branch stops winning it aborts cleanly; if teammates
 * are freed mid-approach, the score drops and the decorator aborts before the
 * hider reaches the ring, so no unnecessary liberation attempt occurs.
 *
 * Debug session p11-uat-round4-fixes (2026-07-05).
 */
export function PathTowardSampledTarget(this: AgentContext & { _sampledTarget?: { x: number; y: number } }): typeof State.SUCCEEDED | typeof State.FAILED | typeof State.RUNNING {
  if (!this._sampledTarget) return State.FAILED;
  const self = this.perception.self.position;
  const dx = this._sampledTarget.x - self.x;
  const dy = this._sampledTarget.y - self.y;
  const len = Math.hypot(dx, dy);
  if (len < 8) return State.SUCCEEDED; // arrived — let BT restart to pick fresh waypoint
  const move = aStarMoveVector(self, this._sampledTarget, this.perception.map);
  if (!move) {
    // Unreachable or same tile — scatter radially away from the plinth ring so
    // hiders spawned at the plinth center (where the ring obstacle blocks all
    // adjacent tiles, isolating the A* source) always produce non-zero movement
    // instead of freezing. Mirrors the fallback used by PathTowardSpot and
    // EnterHideSpot. Affects any entity whose sampled target is unreachable,
    // including hunters pathing to an enclosed blackboard sighting — moving away
    // from the plinth is a reasonable fallback for them too.
    // Debug session hunter-no-chase-facing REOPENED (2026-07-04).
    this.intent.move = radialScatterAwayFromPlinth(self, this.perception);
    return State.RUNNING; // keep moving (scatter) until clear of ring and arrived
  }
  this.intent.move = move;
  return State.RUNNING; // commit to this waypoint until arrival
}

// ---------------------------------------------------------------------------
// Retreat-when-afraid branch actions
// ---------------------------------------------------------------------------

/**
 * FindNearestLitSpot — finds the nearest permanent light position in map.lights.
 * Writes position to this._retreatTarget (used by PathTowardRetreatTarget).
 * Falls back to (0, 0) if no lights are authored (shouldn't happen on real map).
 */
export function FindNearestLitSpot(this: AgentContext & { _retreatTarget?: { x: number; y: number } }): typeof State.SUCCEEDED {
  const lights = this.perception.map.lights.filter(l => l.type === 'permanent');
  if (lights.length === 0) {
    this._retreatTarget = { x: 0, y: 0 };
    return State.SUCCEEDED;
  }
  const self = this.perception.self.position;
  let nearest = lights[0]!;
  let nearestDist = Math.hypot(nearest.position.x - self.x, nearest.position.y - self.y);
  for (let i = 1; i < lights.length; i++) {
    const l = lights[i]!;
    const d = Math.hypot(l.position.x - self.x, l.position.y - self.y);
    if (d < nearestDist) {
      nearestDist = d;
      nearest = l;
    }
  }
  this._retreatTarget = { ...nearest.position };
  return State.SUCCEEDED;
}

/**
 * PathTowardRetreatTarget — moves toward the previously found lit retreat
 * position, routed around walls via A*.
 */
export function PathTowardRetreatTarget(this: AgentContext & { _retreatTarget?: { x: number; y: number } }): typeof State.SUCCEEDED | typeof State.FAILED {
  if (!this._retreatTarget) return State.FAILED;
  const self = this.perception.self.position;
  const dx = this._retreatTarget.x - self.x;
  const dy = this._retreatTarget.y - self.y;
  const len = Math.hypot(dx, dy);
  if (len < 8) return State.SUCCEEDED;
  const move = aStarMoveVector(self, this._retreatTarget, this.perception.map);
  if (!move) return State.SUCCEEDED;
  this.intent.move = move;
  return State.SUCCEEDED;
}

// ---------------------------------------------------------------------------
// Flick-torch-to-listen branch actions (D-40, D-42, D-43)
// ---------------------------------------------------------------------------

/**
 * StopMovement — zeroes the move intent. Always SUCCEEDS.
 */
export function StopMovement(this: AgentContext): typeof State.SUCCEEDED {
  this.intent.move = { x: 0, y: 0 };
  return State.SUCCEEDED;
}

/**
 * FlickTorchOff — sets toggleTorch=true if the torch is currently on.
 * One-shot: consumed by tick.ts; only fires when torch was on.
 */
export function FlickTorchOff(this: AgentContext): typeof State.SUCCEEDED {
  if (this.perception.self.torchOn) {
    this.intent.actions.toggleTorch = true;
  }
  return State.SUCCEEDED;
}

/**
 * FlickTorchOn — sets toggleTorch=true if the torch is currently OFF.
 * One-shot: consumed by tick.ts; only fires when torch was off.
 * Used by the chase branch to ensure the torch is on when actively chasing (D-11).
 *
 * Guard on !torchOn prevents per-tick flicker: once the torch is on, the next
 * decide cycle finds torchOn=true and skips the toggle (Pitfall 1, RESEARCH §Pitfall 1).
 * No new WorldState/Entity fields — golden hash unaffected.
 */
export function FlickTorchOn(this: AgentContext): typeof State.SUCCEEDED {
  if (!this.perception.self.torchOn) {
    this.intent.actions.toggleTorch = true;
  }
  return State.SUCCEEDED;
}

/**
 * Listen — no-op action; hearing boost is computed elsewhere (tick.ts) when
 * the hunter is stationary + torch off (D-43 / D-40).
 * Always SUCCEEDS so the flick-torch-to-listen branch stays RUNNING.
 */
export function Listen(this: AgentContext): typeof State.SUCCEEDED {
  // Hearing boost applied by tick.ts when:
  //   entity.role === 'hunter' && !entity.torchOn && |velocity| < P4_LISTEN_STILL_VELOCITY_PX_PER_S
  return State.SUCCEEDED;
}

// ---------------------------------------------------------------------------
// Check-hiding-spot branch actions (D-34)
// ---------------------------------------------------------------------------

/**
 * PickUncheckedSpot — selects a hiding spot not in checkedHidingSpotIds.
 * Writes to this._targetSpotId and this._targetSpotPos.
 */
export function PickUncheckedSpot(this: AgentContext & { _targetSpotId?: string; _targetSpotPos?: { x: number; y: number } }): typeof State.SUCCEEDED | typeof State.FAILED {
  const unchecked = (this.perception.map.hidingSpots ?? []).filter(
    s => !this.checkedHidingSpotIds.has(s.id),
  );
  if (unchecked.length === 0) return State.FAILED;
  // Pick the nearest unchecked spot
  const self = this.perception.self.position;
  let best = unchecked[0]!;
  let bestDist = Math.hypot(best.position.x - self.x, best.position.y - self.y);
  for (let i = 1; i < unchecked.length; i++) {
    const s = unchecked[i]!;
    const d = Math.hypot(s.position.x - self.x, s.position.y - self.y);
    if (d < bestDist) { bestDist = d; best = s; }
  }
  this._targetSpotId = best.id;
  this._targetSpotPos = { ...best.position };
  return State.SUCCEEDED;
}

/**
 * PathTowardSpot — moves toward the target hiding-spot position, routed
 * around walls via A*.
 */
export function PathTowardSpot(this: AgentContext & { _targetSpotPos?: { x: number; y: number } }): typeof State.SUCCEEDED | typeof State.FAILED {
  if (!this._targetSpotPos) return State.FAILED;
  const self = this.perception.self.position;
  const dx = this._targetSpotPos.x - self.x;
  const dy = this._targetSpotPos.y - self.y;
  const len = Math.hypot(dx, dy);
  if (len < 16) return State.SUCCEEDED; // arrived at spot
  const move = aStarMoveVector(self, this._targetSpotPos, this.perception.map);
  // BUG#2c: scatter radially outward on an unreachable target instead of
  // standing still, so the deep branch never stalls before EnterHideSpot runs.
  this.intent.move = move ?? radialScatterAwayFromPlinth(self, this.perception);
  // Return SUCCEEDED so mistreevous advances; AIController re-issues on next decide tick
  // while move intent keeps the entity walking toward the spot (D-05 cadence pattern).
  return State.SUCCEEDED;
}

/**
 * MarkSpotChecked — adds _targetSpotId to checkedHidingSpotIds. Always SUCCEEDS.
 */
export function MarkSpotChecked(this: AgentContext & { _targetSpotId?: string }): typeof State.SUCCEEDED {
  if (this._targetSpotId) {
    this.checkedHidingSpotIds.add(this._targetSpotId);
  }
  return State.SUCCEEDED;
}

// ---------------------------------------------------------------------------
// Patrol branch actions
// ---------------------------------------------------------------------------

/**
 * PickBasePatrolPoint — picks a point near the map centre (plinth zone area).
 * Plan 05 stub: uses a fixed centre-of-map point. Plan 10 wires to zoneCenter('plinth').
 */
export function PickBasePatrolPoint(this: AgentContext & { _patrolTarget?: { x: number; y: number } }): typeof State.SUCCEEDED {
  const cx = (this.perception.map.widthInTiles * this.perception.map.tileWidth) / 2;
  // Plan 06-13: was tileWidth (typo — cosmetic on Mouchao's square tiles, real
  // bug under any non-square grid). The vertical span uses tileHeight.
  const cy = (this.perception.map.heightInTiles * this.perception.map.tileHeight) / 2;
  // Jitter around centre using perception.rng for variety
  const jitter = 3 * this.perception.map.tileWidth;
  this._patrolTarget = {
    x: cx + (this.perception.rng() - 0.5) * jitter,
    y: cy + (this.perception.rng() - 0.5) * jitter,
  };
  return State.SUCCEEDED;
}

/**
 * PickFarPatrolPoint — picks a random walkable point away from the map centre.
 * Plan 05 stub: picks a random point in the outer quarter of the map.
 */
export function PickFarPatrolPoint(this: AgentContext & { _patrolTarget?: { x: number; y: number } }): typeof State.SUCCEEDED {
  const w = this.perception.map.widthInTiles * this.perception.map.tileWidth;
  const h = this.perception.map.heightInTiles * this.perception.map.tileWidth;
  this._patrolTarget = {
    x: this.perception.rng() * w,
    y: this.perception.rng() * h,
  };
  return State.SUCCEEDED;
}

/**
 * PathTowardPatrolPoint — moves toward the cached patrol target, routed
 * around walls via A*. Returns SUCCEEDED when close (target reached —
 * branch re-picks on next decide).
 */
export function PathTowardPatrolPoint(this: AgentContext & { _patrolTarget?: { x: number; y: number } }): typeof State.SUCCEEDED | typeof State.FAILED | typeof State.RUNNING {
  if (!this._patrolTarget) return State.FAILED;
  const self = this.perception.self.position;
  const dx = this._patrolTarget.x - self.x;
  const dy = this._patrolTarget.y - self.y;
  const len = Math.hypot(dx, dy);
  if (len < this.perception.map.tileWidth) return State.SUCCEEDED; // at patrol point
  const move = aStarMoveVector(self, this._patrolTarget, this.perception.map);
  if (!move) return State.SUCCEEDED; // unreachable — caller re-picks next decide
  this.intent.move = move;
  return State.RUNNING;
}

// ---------------------------------------------------------------------------
// Hider-specific actions (Plan 06)
// ---------------------------------------------------------------------------

/**
 * PickBestHidingSpot — selects the highest-quality hiding spot for this hider.
 * For deep-hider archetype: prefers spots the hider's size fits AND not already used.
 * Writes to this._targetSpotId and this._targetSpotPos.
 * Falls back to the nearest unchecked spot if no size-appropriate spot found.
 */
export function PickBestHidingSpot(this: AgentContext & { _targetSpotId?: string; _targetSpotPos?: { x: number; y: number } }): typeof State.SUCCEEDED {
  const allSpots = this.perception.map.hidingSpots ?? [];
  // Prefer spots not yet visited this round. If every spot has already been
  // checked, re-consider ALL of them — a deep hider re-hiding is fine and,
  // crucially, keeps the deep branch producing movement.
  let candidates = allSpots.filter(s => !this.checkedHidingSpotIds.has(s.id));
  if (candidates.length === 0) candidates = allSpots;

  // BUG#2b fix (debug session free-all-misfire-frozen-hider, 2026-06-11): when
  // the map (region) has NO hiding spots at all, this action previously
  // returned FAILED. Because the hider BT selector guards every branch with
  // while(IsWinningBranch, id), no OTHER branch can run when `deep` is the
  // winning archetype — so the hider produced zero intent.move and FROZE. This
  // bit the LIBERATOR most visibly: after firing free-all its liberate branch
  // is permanently suppressed, `deep` wins for deep-preferring profiles (e.g.
  // fiki, deep=0.625 > move=0.36), and with no reachable spot it froze right at
  // the plinth ring. Fall back to a wander target (sentinel _targetSpotId =
  // undefined) so PathTowardSpot/EnterHideSpot keep the hider moving instead of
  // dead-ending the sequence. The deep branch never fails now.
  if (candidates.length === 0) {
    delete this._targetSpotId;
    this._targetSpotPos = pickWanderTarget(this.perception);
    return State.SUCCEEDED;
  }

  const self = this.perception.self.position;
  // BUG#2c (debug session free-all-misfire-frozen-hider, 2026-06-11): pick the
  // nearest REACHABLE spot, not merely the nearest by straight-line distance. On
  // the real Mouchao map (51% blocked) most hiding spots sit against buildings;
  // if the nearest one is unreachable via the ring-aware A*, EnterHideSpot
  // produces no move and a deep-preferring freed hider FREEZES at the ring (the
  // selector guards every branch with while(IsWinningBranch, id) so no other
  // branch can rescue it). Scanning in nearest-first order keeps the common case
  // at a single A* compute.
  const ranked = [...candidates].sort(
    (a, b) =>
      Math.hypot(a.position.x - self.x, a.position.y - self.y) -
      Math.hypot(b.position.x - self.x, b.position.y - self.y),
  );
  const chosen = ranked.find((s) => isSpotReachable(self, s.position, this.perception.map));
  if (!chosen) {
    // No reachable authored spot — fall back to a wander target so the deep
    // branch still produces movement. Sentinel _targetSpotId undefined marks
    // "bare ground, do not toggle hide"; EnterHideSpot scatters away from the
    // ring if even the wander target turns out unreachable.
    delete this._targetSpotId;
    this._targetSpotPos = pickWanderTarget(this.perception);
    return State.SUCCEEDED;
  }
  this._targetSpotId = chosen.id;
  this._targetSpotPos = { ...chosen.position };
  return State.SUCCEEDED;
}

/**
 * EnterHideSpot — keeps moving toward the target hiding spot and fires
 * toggleHide when within entry range.
 *
 * Returns RUNNING while the hider is in transit (dist >= 24 px), continuously
 * updating intent.move via A* so the entity keeps walking on each decide
 * cycle. Returns SUCCEEDED (with toggleHide=true) when the hider arrives.
 *
 * BUG#2 fix (2026-06-11): the original implementation returned RUNNING with
 * NO intent.move update. mistreevous resumes from a RUNNING node on the next
 * decide tick without re-running earlier sequence nodes (PickBestHidingSpot,
 * PathTowardSpot). So on the second and all subsequent decide cycles only
 * EnterHideSpot ran — no move intent was ever set — and the hider froze at
 * the plinth ring boundary after the very first decide cycle. This was
 * invisible in existing tests because they used charlie (move > deep, no
 * hiding spots in map fixture) and never exercised this path.
 */
export function EnterHideSpot(this: AgentContext & { _targetSpotId?: string; _targetSpotPos?: { x: number; y: number } }): typeof State.SUCCEEDED | typeof State.RUNNING {
  if (!this._targetSpotPos) return State.RUNNING;
  const dx = this._targetSpotPos.x - this.perception.self.position.x;
  const dy = this._targetSpotPos.y - this.perception.self.position.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 24) {
    // BUG#2b fix (2026-06-11): a wander fallback target (no _targetSpotId) is
    // bare ground, NOT a real hiding spot — never toggle hide there. Clear the
    // target and SUCCEED so the deep branch re-picks a fresh wander point next
    // decide, keeping the hider dispersing instead of "hiding" on open ground.
    if (!this._targetSpotId) {
      delete this._targetSpotPos;
      return State.SUCCEEDED;
    }
    // Within entry range of a real spot — toggle hide (one-shot, consumed by tick.ts)
    this.intent.actions.toggleHide = true;
    this.checkedHidingSpotIds.add(this._targetSpotId);
    return State.SUCCEEDED;
  }
  // Keep moving toward the spot so this RUNNING node produces non-zero intent
  // on every decide cycle. Without this, the hider freezes after the first
  // decide cycle because PathTowardSpot (which set the move vector) is not
  // re-run while a RUNNING node is in progress.
  const move = aStarMoveVector(
    this.perception.self.position,
    this._targetSpotPos,
    this.perception.map,
  );
  // BUG#2c: aStarMoveVector returns null when the target is unreachable (a spot
  // or wander point on a wall / in an enclosed pocket). This is the RUNNING node
  // — the selector never re-runs PickBestHidingSpot while it is RUNNING, so the
  // hider cannot self-rescue by re-picking and its move intent would stay {0,0}
  // forever (the BUG#2c freeze). Apply a radial-outward scatter so it always
  // walks AWAY from the plinth ring; the next decide re-picks once it has
  // cleared ring contact.
  this.intent.move =
    move ?? radialScatterAwayFromPlinth(this.perception.self.position, this.perception);
  return State.RUNNING;
}

/**
 * PickQuietWaypoint — picks a random map tile away from recent sighting pressure.
 * For constant-mover archetype: avoids tiles where hunters have recently sighted.
 * Writes to this._sampledTarget (used by PathTowardSampledTarget).
 */
export function PickQuietWaypoint(this: AgentContext & { _sampledTarget?: { x: number; y: number } }): typeof State.SUCCEEDED {
  const w = this.perception.map.widthInTiles * this.perception.map.tileWidth;
  const h = this.perception.map.heightInTiles * this.perception.map.tileHeight;
  // Use perception.rng for deterministic waypoint selection (D-06 / Pitfall 10)
  this._sampledTarget = {
    x: this.perception.rng() * w,
    y: this.perception.rng() * h,
  };
  return State.SUCCEEDED;
}

/**
 * PickPlinthApproach — sets a target on the plinth ring's boundary for the
 * liberator archetype. The ring is solid (clampMotionAgainstWalls blocks
 * entry); pathing toward its center naturally stops the hider at the
 * boundary, where the free-all contact-check fires. We pick a point AT
 * the ring boundary on the side closest to the hider (so the path is
 * shortest) with a small jitter for variety. Source: debug session
 * cascade-prevention (2026-04-28).
 */
export function PickPlinthApproach(this: AgentContext & { _sampledTarget?: { x: number; y: number } }): typeof State.SUCCEEDED {
  const plinth = this.perception.map.zones.get('plinth');
  const center = plinth
    ? zoneCenter(plinth)
    : {
        x: (this.perception.map.widthInTiles * this.perception.map.tileWidth) / 2,
        y: (this.perception.map.heightInTiles * this.perception.map.tileHeight) / 2,
      };
  // Aim AT the ring boundary on the hider's side. This ensures path-finding
  // routes to a point where the hider can stand (just outside the ring) and
  // where the free-all contact check fires.
  const self = this.perception.self.position;
  const dx = center.x - self.x;
  const dy = center.y - self.y;
  const dist = Math.hypot(dx, dy);
  const ringRadius = 48; // mirror P3_PLINTH_RING_RADIUS_PX (avoid cross-tier import in BT action)
  // If hider is already inside the ring's "pull" radius, target the center
  // (the wall clamp will hold them at the boundary). Otherwise target the
  // boundary point on their approach line.
  let targetX = center.x;
  let targetY = center.y;
  if (dist > ringRadius * 2) {
    targetX = center.x - (dx / dist) * ringRadius;
    targetY = center.y - (dy / dist) * ringRadius;
  }
  const jitter = this.perception.map.tileWidth * 0.5;
  this._sampledTarget = {
    x: targetX + (this.perception.rng() - 0.5) * jitter,
    y: targetY + (this.perception.rng() - 0.5) * jitter,
  };
  return State.SUCCEEDED;
}

/**
 * DropToFloor — sets the dropToFloor action flag if the profile includes 'on-floor' ability.
 * Returns FAILED if the hider doesn't have the on-floor ability (guards the fallback-drop branch).
 * Source: FEAR-06, D-18, D-32.
 */
export function DropToFloor(this: AgentContext): typeof State.SUCCEEDED | typeof State.FAILED {
  // The profile is looked up by the calling conditions; here we double-check via perception.
  // If the entity has the ability (set via CharacterProfile.abilities in tick.ts)
  // we emit the dropToFloor intent. Otherwise, FAIL so fallback-drop branch fails.
  // Plan 10 wires the real profile abilities lookup; for now trust the condition guard.
  this.intent.actions.dropToFloor = true;
  this.intent.move = { x: 0, y: 0 }; // Stop moving when dropping to floor
  return State.SUCCEEDED;
}

// ---------------------------------------------------------------------------
// Hearing boost helper (D-40, D-43)
// ---------------------------------------------------------------------------

/**
 * effectiveHearingRangeTiles — D-40 hearing boost formula.
 *
 * Applies when:
 *   - entity.role === 'hunter'
 *   - entity.torchOn === false
 *   - isStationary === true (|velocity| < P4_LISTEN_STILL_VELOCITY_PX_PER_S = 8 px/s)
 *
 * Formula: base * (1 + stat * 1.5)
 *
 * @param entity     - Entity-like with role and torchOn.
 * @param profile    - CharacterProfile with hear.base and hear.stat.
 * @param isStationary - True when |velocity| < P4_LISTEN_STILL_VELOCITY_PX_PER_S.
 *                       Callers compute this from tick state; actions.ts stays pure.
 * @returns Effective hearing range in tiles.
 */
export function effectiveHearingRangeTiles(
  entity: Pick<Entity, 'role' | 'torchOn'>,
  profile: Pick<CharacterProfile, 'hear'>,
  isStationary: boolean = false,
): number {
  const boost =
    entity.role === 'hunter' && !entity.torchOn && isStationary
      ? 1 + profile.hear.stat * 1.5
      : 1;
  return profile.hear.base * boost;
}

// ---------------------------------------------------------------------------
// Dog actions (D-24, NPC-01)
// ---------------------------------------------------------------------------

/**
 * EmitBarkEvent — sets intent.actions.shout=true (one-shot).
 * Tick layer interprets shout from a dog entity as 'dog-barked' event.
 * Source: D-24; RESEARCH Pattern 12.
 */
export function EmitBarkEvent(this: AgentContext): typeof State.SUCCEEDED {
  this.intent.actions.shout = true;
  return State.SUCCEEDED;
}

/**
 * SetBarkCooldown — records the current tick as dogBarkLastTick.
 * Used by BarkCooldownElapsed condition to gate subsequent barks.
 */
export function SetBarkCooldown(this: AgentContext & { dogBarkLastTick?: number }): typeof State.SUCCEEDED {
  this.dogBarkLastTick = this.perception.tick;
  return State.SUCCEEDED;
}

/**
 * PickWanderWaypoint — picks a random walkable point in a 10-tile radius.
 * Plan 10 wires wander-zone polygon read via MapData.dogs[].wanderZonePolygonId.
 * For Plan 08: pick random point within 10-tile radius using perception.rng.
 */
export function PickWanderWaypoint(this: AgentContext & { _sampledTarget?: { x: number; y: number } }): typeof State.SUCCEEDED {
  const w = this.perception.map.widthInTiles * this.perception.map.tileWidth;
  const h = this.perception.map.heightInTiles * this.perception.map.tileHeight;
  const radius = 10 * this.perception.map.tileWidth;
  const self = this.perception.self.position;
  const angle = this.perception.rng() * Math.PI * 2;
  const dist = this.perception.rng() * radius;
  this._sampledTarget = {
    x: Math.max(0, Math.min(w, self.x + Math.cos(angle) * dist)),
    y: Math.max(0, Math.min(h, self.y + Math.sin(angle) * dist)),
  };
  return State.SUCCEEDED;
}

/**
 * IdleAtWaypoint — zeroes movement intent. Always SUCCEEDS.
 */
export function IdleAtWaypoint(this: AgentContext): typeof State.SUCCEEDED {
  this.intent.move = { x: 0, y: 0 };
  return State.SUCCEEDED;
}

// ---------------------------------------------------------------------------
// Adult actions (D-27, D-28, NPC-04)
// ---------------------------------------------------------------------------

/**
 * PickCommentary — freshest-event-wins scorer (D-28, Pattern 13).
 * Selects the highest-priority recent commentable event and picks a line index
 * via per-agent RNG. Writes to this.commentaryToEmit.
 */
export function PickCommentary(
  this: AgentContext & {
    pendingCommentableEvents?: Array<{ eventTag: EventTag; tick: number; type: string }>;
    commentaryToEmit?: { eventTag: EventTag; lineIndex: number };
  },
): typeof State.SUCCEEDED | typeof State.FAILED {
  const events = this.pendingCommentableEvents ?? [];
  if (events.length === 0) return State.FAILED;
  // Freshest-event-wins: score by priority + recency (Pattern 13)
  const priorityByType: Record<string, number> = {
    'catch': 3,
    'hider-released': 3,
    'foxguard-called': 2,
    'round-started': 1,
    'round-ended': 1,
  };
  let best = events[0]!;
  let bestScore = -Infinity;
  for (const e of events) {
    const p = priorityByType[e.type] ?? 0;
    const recency = 1 - (this.perception.tick - e.tick) / 60;
    const score = p + recency;
    if (score > bestScore) {
      bestScore = score;
      best = e;
    }
  }
  const pool = getCommentary(best.eventTag);
  if (!pool || pool.length === 0) return State.FAILED;
  const idx = Math.floor(this.perception.rng() * pool.length);
  this.commentaryToEmit = { eventTag: best.eventTag, lineIndex: idx };
  return State.SUCCEEDED;
}

/**
 * EmitCommentary — writes commentaryToEmit to intent.actions.adultComment.
 * Tick layer consumes intent.actions.adultComment to emit 'adult-commented' event
 * and update world.lastAdultCommentaryTick atomically.
 */
export function EmitCommentary(
  this: AgentContext & {
    commentaryToEmit?: { eventTag: EventTag; lineIndex: number };
  },
): typeof State.SUCCEEDED {
  if (this.commentaryToEmit) {
    (this.intent.actions as unknown as Record<string, unknown>)['adultComment'] = this.commentaryToEmit;
  }
  return State.SUCCEEDED;
}

/**
 * AdultIdleNoop — adults never move (NPC-02); zeroes movement. Always SUCCEEDS.
 */
export function AdultIdleNoop(this: AgentContext): typeof State.SUCCEEDED {
  this.intent.move = { x: 0, y: 0 };
  return State.SUCCEEDED;
}

// ---------------------------------------------------------------------------
// Re-export RUNNING for convenience (test files import State from mistreevous)
// ---------------------------------------------------------------------------

export { type AgentContext };
